import { useEffect, useState } from "react";
import {
  ApiError,
  listSectors,
  readAuditorRecord,
  verifyAuditor,
  type AuditorRecord,
  type Sector,
} from "../api/client";
import { Callout } from "../components/Callout";
import { TextAreaField } from "../components/TextAreaField";
import { idempotencyKey } from "../api/idempotency";

/**
 * Screen 20 — one competence record, and the Cl. 7 determination.
 *
 * What is shown is what the auditor asserted, read back through the same reader their own
 * screen 18 uses. One reader means the claim and the assessment cannot come to disagree.
 *
 * **Verifying may narrow the record, and the screen says so afterwards.** A claimed sector
 * outside this certification body's accredited scope is dropped, not rejected — the auditor
 * may legitimately hold competence the body is not accredited for. What was dropped comes
 * back from the server and is shown, because a determination that quietly narrowed a claim
 * would leave a record nobody can explain the shape of.
 *
 * **AI accountability is not a gate here.** A record with it refused can be verified — the
 * fixtures carry exactly that — and what it costs is said instead. The block is at
 * allocation.
 */
export function VerifyCompetenceRecord({
  auditorId,
  onBack,
  onDone,
}: {
  auditorId: string;
  onBack: () => void;
  onDone: () => void;
}) {
  const [record, setRecord] = useState<AuditorRecord | null>(null);
  const [sectors, setSectors] = useState<readonly Sector[]>([]);
  const [dropped, setDropped] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [asking, setAsking] = useState(false);
  const [note, setNote] = useState("");

  const [key] = useState(() => `competence-${idempotencyKey()}`);

  useEffect(() => {
    Promise.all([readAuditorRecord(auditorId), listSectors()])
      .then(([found, list]) => {
        setRecord(found);
        setSectors(list);
      })
      .catch(report);
  }, [auditorId]);

  function report(cause: unknown) {
    setError(cause instanceof ApiError ? cause.message : "The record could not be read.");
  }

  const decide = async (decision: "verify" | "request_more_information") => {
    setError(null);
    setBusy(true);
    try {
      const result = await verifyAuditor(
        auditorId,
        decision === "verify" ? { decision } : { decision, note: note.trim() },
        key,
      );
      if (decision === "verify" && result.dropped && result.dropped.length > 0) {
        // Kept on screen rather than returning to the register: the body has to be told
        // that what it verified is narrower than what was claimed.
        setRecord(result.record);
        setDropped(result.dropped);
        return;
      }
      onDone();
    } catch (cause) {
      report(cause);
    } finally {
      setBusy(false);
    }
  };

  if (record === null) {
    return (
      <>
        <BackLink onBack={onBack} />
        {error ? (
          <p className="problem" role="alert">
            {error}
          </p>
        ) : (
          <p className="sub">Opening the record&hellip;</p>
        )}
      </>
    );
  }

  const label = (index: number) =>
    sectors.find((sector) => sector.index === index)?.label ?? "—";
  const verified = record.verified_at !== null;
  const submitted = record.submitted_at !== null;

  return (
    <>
      <BackLink onBack={onBack} />

      <div className="between" style={{ marginBottom: "16px" }}>
        <div>
          <div className="eyebrow">Auditor register</div>
          <h1>{record.name}</h1>
          <p className="sub">
            {record.position ?? "Auditor"} · {record.engagement ?? "—"} ·{" "}
            <span className="mono">{record.email}</span>
          </p>
        </div>
        <div style={{ textAlign: "right" }}>
          {verified ? (
            <>
              <span className="pill p-ok">Verified</span>
              <div className="sub" style={{ marginTop: "5px" }}>
                {record.verified_by} · {formatMoment(record.verified_at)}
              </div>
            </>
          ) : submitted ? (
            <span className="pill p-wait">Awaiting your verification</span>
          ) : (
            <span className="pill p-mute">Invited — not yet submitted</span>
          )}
        </div>
      </div>

      {error ? (
        <p className="problem" role="alert">
          {error}
        </p>
      ) : null}

      {dropped ? (
        <Callout tone="warn" title="Verified, narrowed to your accredited scope.">
          These claims fell outside it and were dropped rather than rejected —{" "}
          {dropped.join(", ")}. The auditor may hold them legitimately; you are simply not
          accredited to offer them.
        </Callout>
      ) : null}

      {submitted ? (
        <Callout title="Awaiting your verification.">
          {record.name} asserted this record and signed it on {formatMoment(record.signed_at)}.
          Under ISO/IEC 17021-1 Cl.&nbsp;7 the certification body — not the auditor —
          determines competence. Verify each claim against the evidence before approving.
        </Callout>
      ) : (
        <Callout tone="warn" title="Nothing to verify yet.">
          This auditor holds an invitation and has not submitted a competence record.
        </Callout>
      )}

      {record.ai_accountability === false ? (
        <Callout tone="block" title="AI accountability not accepted.">
          This auditor cannot be allocated to any audit while the declaration stands
          refused. You may still verify the record — the block is at allocation, not here.
        </Callout>
      ) : null}

      {record.witnessed_on === null && submitted ? (
        <Callout title="No witnessed audit on record.">
          Cl.&nbsp;7.2.9 requires ongoing performance monitoring including witnessing.
          Complao tracks this as an open item rather than a blocker — schedule one.
        </Callout>
      ) : null}

      <div className="grid2">
        <div className="card">
          <h2>Competence claimed</h2>
          {(record.competence ?? []).length === 0 ? (
            <p className="sub">No competence claimed.</p>
          ) : (
            (record.competence ?? []).map((claim) => (
              <div key={claim.standard} className="plan-ed" style={{ marginBottom: "10px" }}>
                <div className="row" style={{ justifyContent: "space-between" }}>
                  <b>{claim.standard}</b>
                  <span
                    className={claim.level === "Lead auditor" ? "pill p-ok" : "pill p-wait"}
                  >
                    {claim.level}
                  </span>
                </div>
                <div className="sub" style={{ marginTop: "6px" }}>
                  Granted {claim.granted_on} · next review {claim.next_review}
                </div>
                <div style={{ marginTop: "6px" }}>
                  {(claim.sectors ?? []).map((index) => (
                    <div key={index} className="sub" style={{ fontSize: "12px" }}>
                      IAF {String(index).padStart(2, "0")} · {label(index)}
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>

        <div className="card">
          <h2>Qualifications and experience</h2>
          <dl className="kv">
            <dt>Education</dt>
            <dd>{record.education ?? "—"}</dd>
            {(record.certifications ?? []).map((entry) => (
              <div key={entry.certificate_number} style={{ display: "contents" }}>
                <dt>{entry.scheme}</dt>
                <dd>
                  {entry.grade} — <span className="mono">{entry.certificate_number}</span>,
                  expires {entry.expiry}
                </dd>
              </div>
            ))}
            <dt>Management systems</dt>
            <dd>{record.ms_years ?? "—"} years</dd>
            <dt>Technical sector</dt>
            <dd>{record.sector_years ?? "—"} years</dd>
            <dt>Audits led</dt>
            <dd>{record.audits_led ?? "—"}</dd>
            <dt>Audit days</dt>
            <dd>{record.audit_days ?? "—"}</dd>
            <dt>Witnessed audit</dt>
            <dd>
              {record.witnessed_on ? (
                <>
                  {record.witnessed_on}
                  {record.witnessed_by ? ` · ${record.witnessed_by}` : ""}
                </>
              ) : (
                <span className="pill p-wait">Not yet witnessed</span>
              )}
            </dd>
          </dl>
        </div>
      </div>

      <div className="grid2">
        <div className="card">
          <h2>Declarations</h2>
          <dl className="kv">
            <dt>Confidentiality</dt>
            <dd>{signed(record.confidentiality)}</dd>
            <dt>Impartiality</dt>
            <dd>{signed(record.impartiality)}</dd>
            <dt>AI accountability</dt>
            <dd>
              {record.ai_accountability === null ? (
                <span className="pill p-mute">Not yet asserted</span>
              ) : record.ai_accountability ? (
                <span className="pill p-ok">Accepted</span>
              ) : (
                <span className="pill p-red">Not accepted</span>
              )}
            </dd>
            <dt>Platform training</dt>
            <dd>{signed(record.complao_training)}</dd>
            <dt>Signature</dt>
            <dd>
              <span style={{ fontWeight: 500, color: "var(--colour-brand-deep-forest)" }}>
                {record.signature ?? "—"}
              </span>
              <span className="sub"> · {formatMoment(record.signed_at)}</span>
            </dd>
          </dl>

          <div className="hr" />
          <div className="eyebrow">Training</div>
          {(record.training ?? []).length === 0 ? (
            <p className="sub">None recorded.</p>
          ) : (
            (record.training ?? []).map((entry) => (
              <div key={`${entry.course}:${entry.completed_on}`} className="docrow">
                <div className="g">
                  {entry.course}
                  <small>
                    {entry.provider} · {entry.completed_on}
                  </small>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="card">
          <h2>Excluded clients</h2>
          <p className="sub" style={{ marginBottom: "10px" }}>
            Checked against the client organisation at intake. An allocation to an excluded
            client is blocked, not warned.
          </p>
          {(record.exclusions ?? []).length === 0 ? (
            <p className="sub">None declared.</p>
          ) : (
            (record.exclusions ?? []).map((entry) => (
              <div
                key={entry.organisation_name}
                className="plan-ed"
                style={{ marginBottom: "9px" }}
              >
                <b>{entry.organisation_name}</b>
                <div className="sub" style={{ marginTop: "4px" }}>
                  {entry.reason}
                </div>
                {entry.excluded_until ? (
                  <div className="edited-tag" style={{ marginTop: "5px" }}>
                    Excluded until {entry.excluded_until}
                  </div>
                ) : null}
              </div>
            ))
          )}
        </div>
      </div>

      {asking ? (
        <div className="card">
          <h2>Request more information</h2>
          <p className="sub">
            This returns the record to {record.name} for correction and clears any
            verification with it. The note is what they are told.
          </p>
          <TextAreaField
            id="competence-note"
            label="What is missing or wrong"
            value={note}
            onChange={setNote}
            required
            rows={3}
            hint="At least 10 characters — this is the whole of what the auditor has to act on."
          />
          <div className="row" style={{ justifyContent: "flex-end", gap: "10px" }}>
            <button type="button" className="btn sec" onClick={() => setAsking(false)}>
              Cancel
            </button>
            <button
              type="button"
              className="btn"
              disabled={busy || note.trim().length < 10}
              onClick={() => void decide("request_more_information")}
            >
              {busy ? "Returning…" : "Return the record"}
            </button>
          </div>
        </div>
      ) : (
        <div className="row" style={{ justifyContent: "flex-end", gap: "10px" }}>
          <button
            type="button"
            className="btn sec"
            disabled={busy || !submitted}
            onClick={() => setAsking(true)}
          >
            Request more information
          </button>
          <button
            type="button"
            className="btn"
            disabled={busy || verified || !submitted}
            onClick={() => void decide("verify")}
          >
            {verified ? "Verified" : busy ? "Verifying…" : "Verify and activate"}
          </button>
        </div>
      )}
    </>
  );
}

function BackLink({ onBack }: { onBack: () => void }) {
  return (
    <button type="button" className="backlink" onClick={onBack}>
      ← Auditor register
    </button>
  );
}

function signed(value: boolean | null | undefined) {
  if (value === null || value === undefined) {
    return <span className="pill p-mute">Not yet asserted</span>;
  }
  return value ? (
    <span className="pill p-ok">Signed</span>
  ) : (
    <span className="pill p-red">Not signed</span>
  );
}

function formatMoment(value: string | null | undefined): string {
  if (!value) return "—";
  const when = new Date(value);
  return Number.isNaN(when.getTime())
    ? "—"
    : when.toLocaleString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
}
